import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiApi } from '../api';
import BotCharacter from '../components/BotCharacter';
import DecisionFeed from '../components/DecisionFeed';
import { card, PnlCell, Sparkline, downloadJson, INTERVAL_COLORS } from '../components/ArenaUI';

const ALL_TFS = ['1m', '5m', '15m', '1h', '4h', '1d'];
// Todas menos 1d podem disparar entrada (ver brain.py) -- 1d é só informativo
// (poucas amostras de validação pra confiar sozinho). 1m/5m exigem confiança
// mais alta (mais ruidosas) que 15m/1h/4h.
const ENTRY_TFS = new Set(['1m', '5m', '15m', '1h', '4h']);
const ALL_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];

// Fase 2: 3 "cerebros" com POOL UNICO de capital cada (nao fatiado por
// timeframe como a Arena legada em /arena) -- MTFModel decide a direcao
// vendo todos os TFs de uma vez, cada cerebro so difere no perfil de risco
// (Conservador/Moderado/Agressivo). Ver docs/plano-reestruturacao-2026-09.md.

const SENTIMENT_LABEL = {
  risk_on: { label: 'Risco ligado', color: '#34d399' },
  risk_off: { label: 'Risco desligado', color: '#f87171' },
  neutral: { label: 'Neutro', color: '#94a3b8' },
};

// Saude do modelo por simbolo — um cerebro que decide sobre modelo nao
// treinado e cara-coroa; o badge deixa isso visivel junto do resultado.
function ModelHealth({ symbols }) {
  const { data } = useQuery({
    queryKey: ['models-health'],
    queryFn: () => aiApi.get('/models/health').then(r => r.data),
    refetchInterval: 60_000,
  });
  const models = data?.models || [];
  if (!models.length || !symbols?.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
      {symbols.map(s => {
        const relevant = models.filter(m => m.symbol === s);
        if (!relevant.length) return null;
        const trained = relevant.filter(m => m.trained).length;
        const ok = trained === relevant.length;
        const partial = trained > 0 && !ok;
        const color = ok ? '#34d399' : partial ? '#fbbf24' : '#f87171';
        return (
          <span key={s} title={
            ok ? `Todos os ${relevant.length} modelos de ${s} treinados`
               : `${trained}/${relevant.length} modelos de ${s} treinados`
          } style={{
            fontSize: 10, fontWeight: 700, color,
            background: '#0f172a', border: `1px solid ${color}44`,
            borderRadius: 10, padding: '1px 8px',
          }}>
            {s.replace('USDT', '')} {ok ? '✓' : partial ? '◐' : '✗'} modelo
          </span>
        );
      })}
    </div>
  );
}

export default function Brains() {
  const qc = useQueryClient();
  const [capital, setCapital] = useState(3000);
  const [selectedSymbols, setSelectedSymbols] = useState(['BTCUSDT']);
  const toggleSymbol = (s) => setSelectedSymbols(prev =>
    prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

  const { data: arena } = useQuery({
    queryKey: ['brains'],
    queryFn: () => aiApi.get('/brains').then(r => r.data),
    refetchInterval: 5_000,
  });

  const { data: sentiment } = useQuery({
    queryKey: ['sentiment'],
    queryFn: () => aiApi.get('/sentiment').then(r => r.data),
    refetchInterval: 60_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['brains'] });
  };

  const start = useMutation({
    mutationFn: () => aiApi.post('/brains/start', { capital, symbols: selectedSymbols }),
    onSuccess: invalidate,
  });
  const stop = useMutation({ mutationFn: () => aiApi.post('/brains/stop'), onSuccess: invalidate });
  const reset = useMutation({ mutationFn: () => aiApi.post('/brains/reset'), onSuccess: invalidate });
  const tick = useMutation({ mutationFn: () => aiApi.post('/brains/tick'), onSuccess: invalidate });

  const brains = arena?.brains || [];
  const arenaSymbols = arena?.symbols || [];
  const lastSignalBySymbol = arena?.last_signal || {};
  const tfSignalsBySymbol = arena?.tf_signals || {};
  const sent = SENTIMENT_LABEL[sentiment?.bias] || SENTIMENT_LABEL.neutral;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>🧠 Cérebros</h1>
        {arena && (
          <span style={{
            background: arena.running ? '#052e16' : '#1e293b',
            color: arena.running ? '#34d399' : '#64748b',
            borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700,
          }}>{arena.running ? '● AO VIVO' : '○ PARADO'}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {!arena?.running && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#64748b' }}>Capital por cérebro:</span>
              <span style={{ fontSize: 12, color: '#64748b' }}>$</span>
              <input
                type="number" min={100} step={500} value={capital}
                onChange={e => setCapital(Math.max(100, Number(e.target.value)))}
                style={{ width: 80, background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155',
                  borderRadius: 6, padding: '4px 8px', fontSize: 12 }}
              />
            </div>
          )}
          {!arena?.running && (
            <div style={{ display: 'flex', gap: 4 }}>
              {ALL_SYMBOLS.map(s => {
                const on = selectedSymbols.includes(s);
                return (
                  <button key={s} onClick={() => toggleSymbol(s)} title={`${s} entra na leitura de cada cérebro (mais símbolos = mais oportunidade, mais CPU por ciclo)`} style={{
                    background: on ? '#1e3a5f' : '#1e293b', color: on ? '#38bdf8' : '#64748b',
                    border: `1px solid ${on ? '#38bdf8' : '#334155'}`,
                    borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}>{s.replace('USDT', '')}</button>
                );
              })}
            </div>
          )}
          <button onClick={() => reset.mutate()} style={{ background: '#1e293b', color: '#64748b', border: '1px solid #334155', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12 }}>Reset</button>
          <button onClick={() => tick.mutate()} disabled={tick.isPending || !arena?.running} style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12 }}>
            {tick.isPending ? '...' : '⚡ Tick'}
          </button>
          {arena?.running
            ? <button onClick={() => stop.mutate()} style={{ background: '#2d1515', color: '#f87171', border: '1px solid #f87171', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>⏹ Parar</button>
            : <button onClick={() => start.mutate()} disabled={start.isPending || !selectedSymbols.length} style={{ background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                {start.isPending ? 'Iniciando...' : `▶ Iniciar (3 cérebros · ${selectedSymbols.length} símbolo${selectedSymbols.length !== 1 ? 's' : ''})`}
              </button>
          }
          <button onClick={() => downloadJson(
            `brains-relatorio-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`,
            { generated_at: new Date().toISOString(), ...arena }
          )} style={{ background: '#1e293b', color: '#64748b', border: '1px solid #334155', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12 }}>
            📊 Relatório
          </button>
        </div>
      </div>

      {start.isError && (
        <div style={{ ...card, background: '#1c1208', border: '1px solid #f59e0b', padding: '12px 18px', marginBottom: 16, fontSize: 13, color: '#fbbf24' }}>
          ⚠ {start.error?.response?.data?.detail || 'Erro ao iniciar — o MTFModel provavelmente ainda não foi treinado (treine em AI Insights → Treinar MTF).'}
        </div>
      )}

      {/* Sentimento — compartilhado por todos os cérebros/símbolos */}
      <div style={{ ...card, padding: '12px 20px', marginBottom: 16, display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
        <span style={{ color: '#64748b' }}>
          Sentimento: <b style={{ color: sent.color }}>{sent.label}</b>
          {sentiment?.fear_greed_index != null && ` · F&G ${sentiment.fear_greed_index}`}
          {sentiment?.llm_enabled === false && (
            <span style={{ fontSize: 10, color: '#475569' }}> (sem IA — só índice)</span>
          )}
        </span>
      </div>

      {/* Sinal MTF + de cada timeframe individual, por símbolo — só informativo.
          Todas menos 1d (bordas destacadas) podem de fato disparar uma entrada
          quando o MTF combinado sozinho não bate o mínimo de confiança do
          cérebro -- 1m/5m exigem uma leitura mais decisiva (ver brain.py). */}
      {arenaSymbols.map(sym => {
        const lastSignal = lastSignalBySymbol[sym] || {};
        const tfSignals = tfSignalsBySymbol[sym] || {};
        return (
          <div key={sym} style={{ ...card, padding: '12px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: '#e2e8f0' }}>{sym.replace('USDT', '')}</span>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Sinal MTF ({lastSignal.model || '—'}):{' '}
                <b style={{ color: lastSignal.signal === 'buy' ? '#34d399' : lastSignal.signal === 'sell' ? '#f87171' : '#94a3b8' }}>
                  {lastSignal.signal ? lastSignal.signal.toUpperCase() : '—'}
                </b>
                {lastSignal.confidence > 0 && ` (${Math.round(lastSignal.confidence * 100)}%)`}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
              {ALL_TFS.map(tf => {
                const s = tfSignals[tf];
                const tfColor = INTERVAL_COLORS[tf] || '#94a3b8';
                const sigColor = s?.signal === 'buy' ? '#34d399' : s?.signal === 'sell' ? '#f87171' : '#475569';
                return (
                  <div key={tf} style={{
                    textAlign: 'center', padding: '8px 6px', borderRadius: 6,
                    background: '#0f172a',
                    border: `1px solid ${ENTRY_TFS.has(tf) ? tfColor : '#1e293b'}`,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: tfColor }}>{tf}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: sigColor }}>
                      {s?.signal ? s.signal.toUpperCase() : '—'}
                    </div>
                    {s?.confidence > 0 && (
                      <div style={{ fontSize: 10, color: '#64748b' }}>{Math.round(s.confidence * 100)}%</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Feed de decisões ao vivo — o que cada cérebro viu e fez (Fase 1) */}
      {arenaSymbols.length > 0 && <DecisionFeed symbols={arenaSymbols} />}

      {/* Saude do modelo + Cerebros */}
      {arenaSymbols.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <ModelHealth symbols={arenaSymbols} />
        </div>
      )}
      {brains.length === 0 ? (
        <div style={card}>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            Defina o capital e clique em Iniciar. Cada cérebro (Conservador/Moderado/Agressivo) opera com um
            pool único de capital, decidindo entre todos os timeframes de uma vez — em vez de fatiar fixo
            por TF como a Arena legada.
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {[...brains].sort((a, b) => b.pnl_pct - a.pnl_pct).map((b, i) => {
            const positions = b.positions || [];
            return (
              <div key={b.id} style={{ ...card, borderTop: `3px solid ${b.color}`, marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <BotCharacter
                      seed={b.id?.replace('brain_', '')}
                      mood={b.mood}
                      health={b.initial_capital ? b.capital / b.initial_capital : 1}
                      totalTrades={b.total_trades}
                      hasPosition={positions.length > 0}
                      size={48}
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 16 }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{b.emoji} {b.name}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>
                        {b.leverage}x · {b.allow_short ? 'Long+Short' : 'Long only'} · conf {Math.round((b.min_confidence || 0) * 100)}% · conv {Math.round((b.min_conviction || 0) * 100)}%
                      </div>
                    </div>
                  </div>
                  <PnlCell value={b.pnl_pct} size={16} />
                </div>

                <Sparkline data={b.equity_history} color={b.color} initialValue={b.initial_capital} />

                <div style={{ display: 'flex', gap: 16, marginTop: 6, marginBottom: 8, fontSize: 12, flexWrap: 'wrap' }}>
                  <div><span style={{ color: '#64748b' }}>Capital </span><b style={{ color: '#e2e8f0' }}>${b.capital?.toLocaleString()}</b></div>
                  <div><span style={{ color: '#64748b' }}>Win </span><b style={{ color: '#e2e8f0' }} title="Win rate líquido (após taxas e funding)">{b.win_rate_net != null ? b.win_rate_net?.toFixed(0) : b.win_rate?.toFixed(0)}%</b></div>
                  <div><span style={{ color: '#64748b' }}>Trades </span><b style={{ color: '#e2e8f0' }}>{b.total_trades}</b></div>
                  <div><span style={{ color: '#64748b' }}>Custos </span><b style={{ color: '#fbbf24' }} title="Taxas de entrada+saída e funding já pagos (taker 0.05% por perna)">${b.fees_paid?.toFixed(2) ?? '0.00'}</b></div>
                  {b.max_drawdown_pct !== undefined && (
                    <div><span style={{ color: '#64748b' }}>Drawdown </span><b style={{ color: '#fca5a5' }}>-{b.max_drawdown_pct?.toFixed(1)}%</b></div>
                  )}
                </div>

                {positions.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {positions.map((pos, pi) => (
                      <div key={pi} style={{ fontSize: 12, padding: '8px 10px', background: '#0f172a', borderRadius: 6 }}>
                        <b style={{ color: '#e2e8f0' }}>{pos.symbol?.replace('USDT', '')}</b>{' '}
                        <span style={{ color: pos.side === 'long' ? '#34d399' : '#f87171', fontWeight: 700 }}>
                          {pos.side === 'long' ? '▲ LONG' : '▼ SHORT'}
                        </span>
                        {' '}entrada ${pos.entry_price?.toLocaleString()}
                        {pos.pnl_pct !== undefined && (
                          <span style={{ marginLeft: 8 }}><PnlCell value={pos.pnl_pct} size={12} /></span>
                        )}
                        {pos.source && pos.source !== 'MTF' && (
                          <span style={{ marginLeft: 8, fontSize: 10, color: '#94a3b8' }} title="Entrada via um timeframe específico, MTF sozinho não bateu o mínimo">
                            via {pos.source} (exploratória)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: '#475569' }}>Sem posição aberta — esperando sinal.</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
